import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  TextStyle,
  Stack,
  Button,
  Icon,
  Badge,
  Banner,
  Text,
  Heading,
  SkeletonBodyText,
  SkeletonDisplayText,
  Modal,
  TextContainer,
  Popover
} from '@shopify/polaris';
import { CircleTickMajor, StarFilledMinor, StarOutlineMinor } from '@shopify/polaris-icons';
import { useAuth } from '../contexts/AuthContext';
import { getSubscriptionPlans, getUserSubscription, setUserSubscription, cancelSubscription } from '../services/subscriptionService';
import { createSubscription, redirectToCheckout, checkTrialEligibility } from '../services/stripeService';
import { useLocation } from 'react-router-dom';
import { getUserCreditStatus } from '../services/creditService';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { updateLandingPageCache } from '../services/landingPageService';

const SubscriptionPlans = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [plans, setPlans] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userVIPStatus, setUserVIPStatus] = useState({ isVIP: false, vipPlan: null });
  const [activeSales, setActiveSales] = useState({
    subscription: { active: false, name: '', percentage: 0 }
  });
  
  // Trial plan state
  const [trialEligible, setTrialEligible] = useState(false);
  const [trialPlanSettings, setTrialPlanSettings] = useState({ credits: 20, duration_days: 1 });
  const [startingTrial, setStartingTrial] = useState(false);

  // Modal states
  const [priceToolsModalOpen, setPriceToolsModalOpen] = useState(false);
  const [inventoryToolsModalOpen, setInventoryToolsModalOpen] = useState(false);
  const [productToolsModalOpen, setProductToolsModalOpen] = useState(false);
  const [explanationModalOpen, setExplanationModalOpen] = useState(false);
  const [currentExplanation, setCurrentExplanation] = useState('');

  // State for cancel subscription modal
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  // State for billing period toggle (monthly/yearly)
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const yearlyDiscountPercentage = 20; // 20% discount for yearly plans (equivalent to ~2 months free)

  // State for tooltip popover
  const [activePopoverId, setActivePopoverId] = useState(null);

  // Function to calculate yearly price with discount
  const calculateYearlyPrice = (monthlyPrice) => {
    const yearlyPrice = monthlyPrice * 12 * (1 - yearlyDiscountPercentage / 100);
    return yearlyPrice.toFixed(2);
  };

  // Function to round a price to .99
  const roundTo99 = (price) => {
    // Convert to number, floor it, and add 0.99
    return (Math.floor(parseFloat(price)) + 0.99).toFixed(2);
  };

  // Function to get the appropriate price based on billing period
  const getPriceDisplay = (plan) => {
    if (billingPeriod === 'yearly') {
      const yearlyPrice = calculateYearlyPrice(plan.price);
      return {
        price: yearlyPrice,
        period: '/year',
        savings: `Save ${yearlyDiscountPercentage}%`
      };
    } else {
      return {
        price: plan.price.toFixed(2),
        period: '/month',
        savings: ''
      };
    }
  };

  // Helper function to format explanation text with line breaks and bullet points for the modal
  const formatExplanationText = (content) => {
    if (!content) return null;
    
    // Split the content by line breaks
    const lines = content.split('\n');
    
    return (
      <div style={{ color: '#202223', fontSize: '14px', lineHeight: '1.5' }}>
        {lines.map((line, index) => {
          // Check if the line starts with a bullet point (• character or * character)
          const isBulletPoint = line.trim().startsWith('•') || line.trim().startsWith('*');
          
          if (line.trim() === '') {
            // Empty line becomes a margin
            return <div key={index} style={{ marginBottom: '12px' }} />;
          } else if (isBulletPoint) {
            // Bullet point gets proper indentation
            return (
              <div key={index} style={{ display: 'flex', marginBottom: '8px', alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: '16px', paddingRight: '4px' }}>•</div>
                <div style={{ flex: 1 }}>{line.trim().startsWith('•') ? line.trim().substring(1).trim() : line.trim().substring(1).trim()}</div>
              </div>
            );
          } else {
            // Regular text
            return <div key={index} style={{ marginBottom: '8px' }}>{line}</div>;
          }
        })}
      </div>
    );
  };

  // Helper function to format tooltip content with line breaks and bullet points
  const formatTooltipContent = (content) => {
    if (!content) return null;
    
    // Split the content by line breaks
    const lines = content.split('\n');
    
    return (
      <div style={{ maxWidth: '300px', color: '#202223', fontSize: '14px', lineHeight: '1.5' }}>
        {lines.map((line, index) => {
          // Check if the line starts with a bullet point (• character or * character)
          const isBulletPoint = line.trim().startsWith('•') || line.trim().startsWith('*');
          
          if (line.trim() === '') {
            // Empty line becomes a margin
            return <div key={index} style={{ marginBottom: '12px' }} />;
          } else if (isBulletPoint) {
            // Bullet point gets proper indentation
            return (
              <div key={index} style={{ display: 'flex', marginBottom: '8px', alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: '16px', paddingRight: '4px' }}>•</div>
                <div style={{ flex: 1 }}>{line.trim().startsWith('•') ? line.trim().substring(1).trim() : line.trim().substring(1).trim()}</div>
              </div>
            );
          } else {
            // Regular text
            return <div key={index} style={{ marginBottom: '8px' }}>{line}</div>;
          }
        })}
      </div>
    );
  };

  // Fetch subscription plans and user's current subscription
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching subscription data for user:', user?.uid);
        
        // Fetch subscription plans
        const plansData = await getSubscriptionPlans();
        setPlans(plansData);
        console.log('Subscription plans loaded:', plansData);
        
        // Fetch user's subscription if logged in
        if (user) {
          const subscription = await getUserSubscription(user.uid);
          console.log('User subscription data:', subscription);
          setCurrentSubscription(subscription);
          
          // Fetch VIP status
          const creditStatus = await getUserCreditStatus(user.uid);
          setUserVIPStatus({
            isVIP: creditStatus.isVIP || false,
            vipPlan: creditStatus.vipPlan || null
          });
        }
        
        // Fetch active sales
        const salesDoc = await getDoc(doc(db, 'settings', 'sales'));
        if (salesDoc.exists()) {
          const salesData = salesDoc.data();
          setActiveSales(salesData);
          
          // Update the landing page cache with the subscription data
          updateLandingPageCache(plansData, null, null, salesData);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user]);

  // Fetch active sales
  const fetchActiveSales = async () => {
    try {
      const salesRef = doc(db, 'system_settings', 'sales');
      const salesDoc = await getDoc(salesRef);
      
      if (salesDoc.exists()) {
        const salesData = salesDoc.data();
        setActiveSales({
          subscription: {
            active: salesData.subscription?.active || false,
            name: salesData.subscription?.name || '',
            percentage: salesData.subscription?.percentage || 0
          }
        });
      }
    } catch (error) {
      console.error('Error fetching active sales:', error);
    }
  };

  useEffect(() => {
    fetchActiveSales();
  }, []);

  // Handle URL parameters for success/cancel messages
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const success = params.get('success');
    const canceled = params.get('canceled');
    const plan = params.get('plan');
    const isTrial = params.get('trial') === 'true';
    
    if (success === 'true') {
      if (isTrial) {
        setSuccess(`Your trial subscription has been activated successfully! You now have access to premium features.`);
      } else {
        setSuccess(`Your subscription has been updated successfully! You now have access to the ${plan ? plans[plan]?.name || 'new' : 'new'} plan.`);
      }
      
      // Refresh subscription data after successful checkout
      if (user) {
        getUserSubscription(user.uid).then(subscription => {
          setCurrentSubscription(subscription);
          console.log('Updated subscription after successful checkout:', subscription);
        });
      }
    } else if (canceled === 'true') {
      setError('Subscription process was canceled. No changes were made to your subscription.');
    }
  }, [location.search, plans, user]);
  
  // Fetch trial plan settings and check eligibility
  const fetchTrialPlanSettings = async () => {
    try {
      // Fetch trial plan settings
      const settingsRef = doc(db, 'trial_plan_settings', 'settings');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        setTrialPlanSettings({
          credits: settings.credits || 20,
          duration_days: settings.duration_days || 1,
          stripe_product_id: settings.stripe_product_id || '',
          stripe_price_id: settings.stripe_price_id || ''
        });
      }
      
      // Check if user is eligible for trial using server-side validation
      if (user && user.uid) {
        const eligibilityResult = await checkTrialEligibility(user.uid);
        setTrialEligible(eligibilityResult.eligible);
      }
    } catch (error) {
      console.error('Error fetching trial plan settings:', error);
      setTrialEligible(false);
    }
  };

  useEffect(() => {
    fetchActiveSales();
    fetchTrialPlanSettings();
  }, []);

  // Check for URL parameters indicating return from Stripe checkout
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const success = queryParams.get('success');
    const canceled = queryParams.get('canceled');
    const planType = queryParams.get('plan');
    const isTrial = queryParams.get('trial') === 'true';
    
    if (success === 'true' && planType && user) {
      // Handle successful subscription
      const refreshSubscription = async () => {
        try {
          setLoading(true);
          setError('');
          
          // Add a delay to ensure Stripe webhook has processed
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Fetch subscription plans if they're not loaded yet
          if (Object.keys(plans).length === 0) {
            const plansData = await getSubscriptionPlans();
            setPlans(plansData);
          }
          
          // Refresh user subscription data
          const subscription = await getUserSubscription(user.uid);
          setCurrentSubscription(subscription);
          
          const creditStatus = await getUserCreditStatus(user.uid);
          setUserVIPStatus({ isVIP: creditStatus.isVIP, vipPlan: creditStatus.vipPlan });
          
          // Show success message
          if (isTrial) {
            setSuccess(`Successfully started your ${trialPlanSettings.duration_days}-day trial with ${trialPlanSettings.credits} credits!`);
            // User is no longer eligible for trials
            setTrialEligible(false);
          } else {
            setSuccess(`Successfully subscribed to the ${plans[planType]?.name || planType} plan!`);
          }
          
          // Clear the URL parameters
          window.history.replaceState({}, document.title, '/app/subscription');
          
          setLoading(false);
        } catch (err) {
          console.error('Error refreshing subscription data:', err);
          setError('There was an issue verifying your subscription. Please contact support if the problem persists.');
          setLoading(false);
        }
      };
      
      refreshSubscription();
    } else if (canceled === 'true') {
      // Handle canceled checkout
      setError('Subscription checkout was canceled. No changes were made to your account.');
      // Clear the URL parameters
      window.history.replaceState({}, document.title, '/app/subscription');
    }
  }, [user, plans, trialPlanSettings]);

  // Check for expired subscription from location state
  useEffect(() => {
    const expired = window.location.search.includes('expired=true') || 
                   (location.state && location.state.expired);
    
    if (expired) {
      setError('');
      setSuccess('');
      setError('Your subscription has expired. Please select a plan to continue using the application.');
    }
  }, [location]);

  // Handle subscription change
  const handleSubscriptionChange = async (planType) => {
    // Don't allow subscription changes if user has VIP status
    if (userVIPStatus.isVIP && userVIPStatus.vipPlan) {
      setError('You currently have VIP access. Please contact an administrator if you wish to change your plan.');
      return;
    }
    
    try {
      setError('');
      setProcessingPlan(planType);
      
      // If user already has an active subscription, show confirmation
      if (currentSubscription && currentSubscription.status === 'active') {
        setSelectedPlan(planType);
        return;
      }
      
      // Create base URL for success and cancel URLs
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/app/subscription?success=true&plan=${planType}&billing=${billingPeriod}`;
      const cancelUrl = `${baseUrl}/app/subscription?canceled=true`;
      
      // Create a Stripe checkout session with billing period
      const result = await createSubscription(
        user.uid, 
        planType, 
        successUrl, 
        cancelUrl, 
        billingPeriod // Pass billing period to the subscription creation function
      );
      
      console.log('Subscription creation result:', result);
      
      if (result.success && result.sessionUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.sessionUrl;
      } else {
        throw new Error(result.error || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Error changing subscription:', error);
      setError(error.message || 'Failed to change subscription. Please try again.');
    } finally {
      setProcessingPlan(null);
    }
  };
  
  // Handle starting a trial subscription
  const handleStartTrial = async (planType) => {
    try {
      setError('');
      setStartingTrial(true);
      
      // Create the trial subscription
      const successUrl = `${window.location.origin}/app/subscription?success=true&plan=${planType}&trial=true`;
      const cancelUrl = `${window.location.origin}/app/subscription?canceled=true`;
      
      const result = await createSubscription(
        user.uid,
        planType,
        successUrl,
        cancelUrl,
        'monthly', // Trial is always monthly
        true // isTrial flag
      );
      
      if (result.success && result.sessionUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.sessionUrl;
      } else {
        throw new Error(result.error || 'Failed to start trial subscription');
      }
    } catch (error) {
      console.error('Error starting trial:', error);
      setError(error.message || 'Failed to start trial. Please try again.');
    } finally {
      setStartingTrial(false);
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    if (!user || !currentSubscription || !currentSubscription.stripeSubscriptionId) {
      setError('No active subscription found');
      setCancelModalOpen(false);
      return;
    }
    
    try {
      setCancellingSubscription(true);
      setError('');
      
      const result = await cancelSubscription(user.uid, currentSubscription.stripeSubscriptionId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel subscription');
      }
      
      // Update the local subscription state
      setCurrentSubscription({
        ...currentSubscription,
        status: 'canceling'
      });
      
      setSuccess('Your subscription will be canceled at the end of the current billing period.');
      setCancelModalOpen(false);
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError('Failed to cancel subscription. Please try again later.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  // Helper function to format date safely
  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    
    // Handle Firestore timestamp objects
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleDateString();
    }
    
    // Handle regular date strings
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return null;
    
    return date.toLocaleDateString();
  };

  // CSS styles for consistent card layout
  const styles = {
    cardWrapper: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    card: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    },
    iconContainer: {
      height: '24px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '0.5rem'
    },
    badgeContainer: {
      margin: '0.5rem 0',
      height: '28px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    popularBadgeContainer: {
      margin: '0.5rem 0',
      height: '28px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px'
    },
    featureList: {
      marginBottom: '1.5rem',
      flex: 1,
      minHeight: '200px' // Increased fixed height for feature list
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      minHeight: '30px'
    },
    checkIcon: {
      color: '#008060',
      width: '20px',
      textAlign: 'center',
      marginRight: '8px'
    },
    buttonContainer: {
      textAlign: 'center',
      marginTop: 'auto',
      paddingTop: '1rem'
    },
    planHeader: {
      textAlign: 'center',
      marginBottom: '1rem',
      minHeight: '180px', // Fixed height for the plan header section
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start'
    },
    popularBadge: {
      height: '28px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  };

  // Helper function to render star icons based on plan level
  const renderStars = (planType) => {
    // Extract plan number from plan type (e.g., 'plan_3' -> 3)
    const planNumber = parseInt(planType.split('_')[1]) || 1;
    
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '2px' }}>
        {Array(5).fill().map((_, index) => (
          <Icon 
            key={index} 
            source={index < planNumber ? StarFilledMinor : StarOutlineMinor} 
            color="warning" 
          />
        ))}
      </div>
    );
  };

  // Render feature list with checkmarks
  const renderFeatures = (features) => {
    return (
      <div style={styles.featureList}>
        {features.map((feature, index) => (
          <div key={index} style={styles.featureItem}>
            {feature ? (
              <>
                <div style={styles.checkIcon}>
                  <Icon source={CircleTickMajor} color="success" />
                </div>
                <TextStyle>{feature}</TextStyle>
              </>
            ) : (
              <div style={{ height: '30px' }}></div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render trial plan banner if user is eligible
  const renderTrialBanner = () => {
    // Only show trial banner if user is eligible, doesn't have an active subscription, and is not a VIP user
    if (trialEligible && !currentSubscription?.status && !userVIPStatus.isVIP && plans && plans['plan_3']) {
      // Use Plan 3 (Premium) as the reference plan for trial features
      const premiumPlan = plans['plan_3'];
      
      return (
        <div style={{
          marginBottom: '32px',
          border: '2px solid #008060',
          borderRadius: '8px',
          boxShadow: '0 0 15px rgba(0, 128, 96, 0.3)',
          overflow: 'hidden'
        }}>
          <Card>
            <div style={{
              background: 'linear-gradient(90deg, #008060 0%, #00a76f 100%)',
              padding: '16px',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <Heading element="h2">Try Premium Features for Free</Heading>
                <p style={{ margin: '8px 0 0 0' }}>
                  Get {trialPlanSettings.credits} credits for {trialPlanSettings.duration_days} day{trialPlanSettings.duration_days > 1 ? 's' : ''} - No charge during trial
                </p>
              </div>
              <Badge status="success">FREE TRIAL</Badge>
            </div>
            
            <Card.Section>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: '2' }}>
                  <TextContainer>
                    <Heading>Premium Plan Features</Heading>
                    <p>Experience all premium features with our free trial:</p>
                  </TextContainer>
                  
                  <div style={{ marginTop: '16px' }}>
                    {premiumPlan.features && renderFeatures(premiumPlan.features)}
                  </div>
                </div>
                
                <div style={{ flex: '1', marginLeft: '32px', textAlign: 'center' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <TextStyle variation="subdued">Trial includes:</TextStyle>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', margin: '8px 0' }}>
                      {trialPlanSettings.credits} Credits
                    </div>
                    <TextStyle variation="subdued">for {trialPlanSettings.duration_days} day{trialPlanSettings.duration_days > 1 ? 's' : ''}</TextStyle>
                  </div>
                  
                  <Button
                    primary
                    size="large"
                    onClick={() => handleStartTrial('plan_3')}
                    loading={startingTrial}
                  >
                    Start Free Trial
                  </Button>
                  
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#637381' }}>
                    Credit card required for verification. Trial does not auto-convert.
                  </div>
                </div>
              </div>
            </Card.Section>
          </Card>
        </div>
      );
    }
    return null;
  };

  // Render plan cards
  const renderPlanCards = () => {
    
    if (loading) {
      return Array(5).fill().map((_, index) => (
        <Layout.Section oneThird key={index}>
          <Card sectioned>
            <SkeletonDisplayText size="small" />
            <div style={{ margin: '1rem 0' }}>
              <SkeletonDisplayText size="large" />
            </div>
            <SkeletonBodyText lines={4} />
          </Card>
        </Layout.Section>
      ));
    }

    if (!plans || Object.keys(plans).length === 0) {
      return (
        <Layout.Section>
          <Card sectioned>
            <TextStyle variation="subdued">No subscription plans available.</TextStyle>
          </Card>
        </Layout.Section>
      );
    }
    
    // Find the plan with the most features to determine max height
    const maxFeatures = Math.max(...Object.values(plans).map(plan => plan.features ? plan.features.length : 0));
    
    return Object.entries(plans).map(([planType, plan]) => {
      // Only consider it the current plan if subscription is active or canceling
      const isCurrentPlan = (currentSubscription?.type === planType && 
                           currentSubscription?.status && 
                           (currentSubscription.status === 'active' || 
                            currentSubscription.status === 'canceling')) ||
                           // Also consider it current plan if user has VIP access to this plan
                           (userVIPStatus.isVIP && userVIPStatus.vipPlan === planType);
      
      // Disable plan selection if user has VIP status
      const hasVIPAccess = userVIPStatus.isVIP && userVIPStatus.vipPlan;
      const isProcessing = processingPlan === planType;
      // Explicitly set Premium (plan_3) as the most popular
      const isPopular = planType === 'plan_3';

      // Ensure all plans have the same feature list height by padding with empty space if needed
      const paddedFeatures = [...(plan.features || [])];
      while (paddedFeatures.length < maxFeatures) {
        paddedFeatures.push('');
      }

      const priceDisplay = getPriceDisplay(plan);

      return (
        <Layout.Section oneThird key={planType}>
          <div style={styles.cardWrapper}>
            <Card
              sectioned
              style={{
                ...styles.card,
                border: isCurrentPlan ? '2px solid #008060' : '',
                boxShadow: isPopular ? '0 0 10px rgba(0, 128, 96, 0.3)' : '',
                height: '100%', // Ensure all cards have the same height
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={styles.planHeader}>
                <div style={styles.iconContainer}>
                  {renderStars(planType)}
                </div>
                <Heading element="h2">{plan.name}</Heading>
                <div style={styles.popularBadge}>
                  {isPopular && (
                    <Badge status="success">MOST POPULAR</Badge>
                  )}
                </div>
                {plan.description && (
                  <div style={{ marginTop: '0.5rem', minHeight: '40px' }}>
                    <TextStyle variation="subdued">{plan.description}</TextStyle>
                  </div>
                )}
                <div style={{ margin: '1rem 0' }}>
                  <Text variant="headingXl" as="p">
                    ${priceDisplay.price}
                    <Text variant="bodyMd" as="span" color="subdued">{priceDisplay.period}</Text>
                  </Text>
                  {plan.compareAtPrice && activeSales.subscription.active && (
                    <div style={{ marginTop: '0.25rem' }}>
                      <Text variant="bodyMd" as="p" color="subdued">
                        {billingPeriod === 'yearly' ? (
                          // For yearly, apply the yearly discount to compareAtPrice and round to .99
                          <s>${roundTo99(calculateYearlyPrice(plan.compareAtPrice))}</s>
                        ) : (
                          // For monthly, use compareAtPrice directly with rounding
                          <s>${roundTo99(plan.compareAtPrice.toString())}</s>
                        )}
                        <Text variant="bodyMd" as="span" color="critical">{activeSales.subscription.percentage}% off</Text>
                      </Text>
                    </div>
                  )}
                  {priceDisplay.savings && (
                    <div style={{ marginTop: '0.25rem' }}>
                      <Text variant="bodyMd" as="p" color="success">{priceDisplay.savings}</Text>
                    </div>
                  )}
                </div>
                <TextStyle variation="subdued">
                  {plan.monthlyCredits} credits per month
                </TextStyle>
                <div style={styles.badgeContainer}>
                  <Badge status="info">Edit up to {plan.maxProductSelection} products at once</Badge>
                </div>
                {plan.productCountText && (
                  <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <Text variant="bodyMd" as="p">
                      {plan.productCountText}
                      {plan.productCountExplanation && (
                        <Button
                          plain
                          monochrome
                          icon={<span style={{ fontSize: '16px', marginLeft: '4px' }}>&#x24D8;</span>}
                          onClick={() => {
                            setCurrentExplanation(plan.productCountExplanation);
                            setExplanationModalOpen(true);
                          }}
                          accessibilityLabel="View explanation"
                        />
                      )}
                    </Text>
                  </div>
                )}
              </div>
              
              <div style={{ flex: 1 }}>
                {renderFeatures(paddedFeatures)}
              </div>
              
              <div style={styles.buttonContainer}>
                {isCurrentPlan ? (
                  <Button disabled>Current Plan</Button>
                ) : (
                  <Stack vertical spacing="tight">
                    <Button
                      primary={isPopular}
                      onClick={() => handleSubscriptionChange(planType)}
                      loading={isProcessing}
                      disabled={hasVIPAccess}
                    >
                      {currentSubscription ? 'Switch to this Plan' : 'Select Plan'}
                    </Button>
                    
                    {/* Trial button removed - now using dedicated trial banner above */}
                  </Stack>
                )}
              </div>
            </Card>
          </div>
        </Layout.Section>
      );
    });
  };

  // Tool description modals
  const renderPriceToolsModal = (
    <Modal
      open={priceToolsModalOpen}
      onClose={() => setPriceToolsModalOpen(false)}
      title="Price Tools"
      primaryAction={{
        content: 'Close',
        onAction: () => setPriceToolsModalOpen(false),
      }}
    >
      <Modal.Section>
        <TextContainer>
          <p>Our Price Tools provide powerful capabilities for managing your product pricing:</p>
          <ul>
            <li><strong>Bulk Price Updates</strong> - Modify prices across multiple products at once using percentage or fixed amount adjustments</li>
            <li><strong>Price Change Types</strong> - Increase, decrease, or set specific prices with flexible options</li>
            <li><strong>Compare-at-Price Management</strong> - Easily set and adjust compare-at prices to show savings</li>
            <li><strong>Price Rounding</strong> - Automatically round prices to maintain consistent pricing patterns</li>
            <li><strong>Save & Load Settings</strong> - Save your pricing strategies for future use</li>
          </ul>
          <p>These tools help you optimize your pricing strategy and save time on manual price management tasks.</p>
        </TextContainer>
      </Modal.Section>
    </Modal>
  );

  const renderInventoryToolsModal = (
    <Modal
      open={inventoryToolsModalOpen}
      onClose={() => setInventoryToolsModalOpen(false)}
      title="Inventory Tools"
      primaryAction={{
        content: 'Close',
        onAction: () => setInventoryToolsModalOpen(false),
      }}
    >
      <Modal.Section>
        <TextContainer>
          <p>Our Inventory Tools help you efficiently manage your product stock:</p>
          <ul>
            <li><strong>Bulk Inventory Updates</strong> - Update inventory quantities across multiple products simultaneously</li>
            <li><strong>Selective Updates</strong> - Option to ignore products with zero inventory</li>
            <li><strong>Save & Load Settings</strong> - Store your inventory management preferences</li>
          </ul>
          <p>These tools streamline your inventory management process and help maintain accurate stock levels across your store.</p>
        </TextContainer>
      </Modal.Section>
    </Modal>
  );

  const renderProductToolsModal = (
    <Modal
      open={productToolsModalOpen}
      onClose={() => setProductToolsModalOpen(false)}
      title="Product Tools"
      primaryAction={{
        content: 'Close',
        onAction: () => setProductToolsModalOpen(false),
      }}
    >
      <Modal.Section>
        <TextContainer>
          <p>Our Product Tools give you advanced capabilities for managing your product catalog:</p>
          <ul>
            <li><strong>Handle Management</strong> - Generate custom handles with prefixes/suffixes or empty handles for Shopify auto-generation</li>
            <li><strong>Vendor Management</strong> - Update vendor information across multiple products</li>
            <li><strong>SKU Generator</strong> - Create SKUs for all variants using customizable format with product title, variant options, and prefixes/suffixes</li>
            <li><strong>Save & Load Settings</strong> - Store your product management preferences for future use</li>
          </ul>
          <p>These tools help you maintain a well-organized product catalog and make bulk changes efficiently.</p>
        </TextContainer>
      </Modal.Section>
    </Modal>
  );

  const renderExplanationModal = (
    <Modal
      open={explanationModalOpen}
      onClose={() => setExplanationModalOpen(false)}
      title="How We Calculate Product Edits"
    >
      <Modal.Section>
        <div 
          className="html-content-container"
          style={{ padding: '0 20px' }}
          dangerouslySetInnerHTML={{ __html: currentExplanation }}
        />
      </Modal.Section>
    </Modal>
  );

  return (
    <Page title="Subscription Plans">
      {error && (
        <Banner status="critical" title="Error" onDismiss={() => setError('')}>
          {error}
        </Banner>
      )}
      
      {success && (
        <Banner status="success" title="Success" onDismiss={() => setSuccess('')}>
          {success}
        </Banner>
      )}
      
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <Stack vertical spacing="loose">
              <Stack.Item>
                <TextStyle variation="subdued">
                  Choose the plan that's right for you. All plans include access to core features.
                </TextStyle>
              </Stack.Item>
              
              {userVIPStatus.isVIP && userVIPStatus.vipPlan && (plans[userVIPStatus.vipPlan] || currentSubscription?.isVIP) && (
                <Stack.Item>
                  <Banner
                    title="VIP Access"
                    status="success"
                  >
                    <p>You have VIP access to the <strong>{plans[userVIPStatus.vipPlan]?.name || currentSubscription?.planName || 'Premium'}</strong> plan. Enjoy all features without any charges!</p>
                  </Banner>
                </Stack.Item>
              )}
              
              {/* Only show the current subscription banner if the user has a regular subscription and is not a VIP user */}
              {currentSubscription && currentSubscription.type && currentSubscription.status && 
               (currentSubscription.status === 'active' || currentSubscription.status === 'canceling') && 
               (!userVIPStatus.isVIP || !userVIPStatus.vipPlan) && (
                <Stack.Item>
                  <Banner
                    title="Current Subscription"
                    status="info"
                  >
                    <p>{currentSubscription.type && plans[currentSubscription.type] ? (
                      <>You are currently on the <strong>{plans[currentSubscription.type].name}</strong> plan.</>
                    ) : (
                      <>Your subscription is active.</>
                    )}</p>
                    {currentSubscription.status === 'canceling' ? (
                      <p>
                        Your subscription has been canceled and will end on {formatDate(currentSubscription.currentPeriodEnd)}.
                        You can continue to use your subscription until this date.
                      </p>
                    ) : (
                      <p>
                        You can manage your subscription from the Payment History page.
                      </p>
                    )}
                  </Banner>
                </Stack.Item>
              )}
              
              {/* Display sale banner if active */}
              {activeSales.subscription.active && (
                <Stack.Item>
                  <Banner
                    title={activeSales.subscription.name}
                    status="warning"
                  >
                    <p>Save {activeSales.subscription.percentage}% on all subscription plans for a limited time!</p>
                  </Banner>
                </Stack.Item>
              )}
            </Stack>
          </Card>
        </Layout.Section>
      </Layout>
      
      <div style={{ marginTop: '3rem', marginBottom: '2rem', display: 'flex', justifyContent: 'center' }}>
        <Card sectioned>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <Text variant="headingMd">Billing Period</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Button 
                onClick={() => setBillingPeriod('monthly')} 
                primary={billingPeriod === 'monthly'}
                outline={billingPeriod !== 'monthly'}
              >
                Monthly
              </Button>
              <Button 
                onClick={() => setBillingPeriod('yearly')} 
                primary={billingPeriod === 'yearly'}
                outline={billingPeriod !== 'yearly'}
              >
                Yearly (Save {yearlyDiscountPercentage}%)
              </Button>
            </div>
            {billingPeriod === 'yearly' && (
              <Text variant="bodyMd" color="success">Save approximately 2 months when you pay annually!</Text>
            )}
          </div>
        </Card>
      </div>
      
      {/* Trial banner above subscription plans */}
      {renderTrialBanner()}
      
      {/* Plan cards in a single row */}
      <div style={{ marginTop: '20px' }}>
        <Layout>
          {renderPlanCards()}
        </Layout>
      </div>
      
      {/* Tools showcase section */}
      <div style={{ marginTop: '20px' }}>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <Stack vertical spacing="tight">
                <Stack.Item>
                  <Heading element="h3">Explore Our Tools</Heading>
                </Stack.Item>
                <Stack.Item>
                  <p>Learn more about the powerful tools included in all subscription plans. <strong style={{ color: '#008060' }}>Using these tools is completely FREE and UNLIMITED</strong> - they will never consume any of your credits:</p>
                </Stack.Item>
                <Stack.Item>
                  <div style={{ marginTop: '1rem' }}>
                    <Stack distribution="fillEvenly">
                      <Button onClick={() => setPriceToolsModalOpen(true)}>Price Tools</Button>
                      <Button onClick={() => setInventoryToolsModalOpen(true)}>Inventory Tools</Button>
                      <Button onClick={() => setProductToolsModalOpen(true)}>Product Tools</Button>
                    </Stack>
                  </div>
                </Stack.Item>
              </Stack>
            </Card>
          </Layout.Section>
        </Layout>
      </div>
      
      {/* Need more credits section at the bottom */}
      <div style={{ marginTop: '20px' }}>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <Stack vertical spacing="tight">
                <Stack.Item>
                  <Heading element="h3">Need more credits?</Heading>
                </Stack.Item>
                <Stack.Item>
                  <p>If you need additional credits beyond your monthly allocation, you can purchase flex credit packages.</p>
                </Stack.Item>
                <Stack.Item>
                  <div style={{ marginTop: '1rem' }}>
                    <Button url="/credits">View Flex Credit Packages</Button>
                  </div>
                </Stack.Item>
              </Stack>
            </Card>
          </Layout.Section>
        </Layout>
      </div>
      
      {renderPriceToolsModal}
      {renderInventoryToolsModal}
      {renderProductToolsModal}
      {renderExplanationModal}
    </Page>
  );
};

export default SubscriptionPlans;
